import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, map } from 'rxjs';
import { AlertService, BalanceBlockComponent, DialogService, NotifierService, ThemeService } from '@wm-libs/core';
import { AccountsService } from '../../services/accounts.service';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { UserService } from '@wm-libs/core';
import { Account } from '../../interfaces/accounts.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatOptionModule } from '@angular/material/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgClass, NgStyle, NgFor, AsyncPipe, NgIf } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { Validators } from '@angular/forms';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [MatListModule, NgIf, NgClass, NgStyle, MatFormFieldModule, BalanceBlockComponent, MatSelectModule, NgFor, MatOptionModule, RouterLink, RouterLinkActive, AsyncPipe, TranslateModule],
})
export class SidebarComponent implements OnInit, OnDestroy {
  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));
  isTablet$ = this.breakpointObserver.observe([Breakpoints.Tablet, Breakpoints.Medium]).pipe(map((result) => result.matches));

  accounts!: Account[] | null;
  accountsSubscription!: Subscription;
  userSubscription!: Subscription;

  isDarkMode$!: boolean;
  selectedFarm!: Account | null;
  tabDisabled = false;
  accountsLoaded = false;
  balance = 0;

  @ViewChild('farmSelect') farmSelect!: MatSelect;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private accountsService: AccountsService,
    public dialog: MatDialog,
    private themeService: ThemeService,
    private notifier: NotifierService,
    private dialogService: DialogService,
    private userService: UserService,
    private alertService: AlertService,
    private accountService: AccountsService,
    private translateService: TranslateService,
    private router: Router
  ) {
    this.themeService.isDarkMode$.subscribe((isDarkMode) => {
      this.isDarkMode$ = isDarkMode;
    });
  }

  ngOnInit(): void {
    this.userSubscription = this.userService.userData$.subscribe(() => {
      this.tabDisabled = false;
    });

    this.accountsSubscription = this.accountsService.accounts$.subscribe((accounts) => {
      this.accounts = accounts;

      if (!this.accounts?.length) {
        return;
      }

      for (const account of this.accounts) {
        if (account.current) {
          this.selectedFarm = account;
          this.balance = this.selectedFarm.balance;
        }
      }
    });

    this.accountsService.getAccounts().subscribe(() => {
      this.accountsLoaded = true;

      if (this.router.url !== '/account') {
        this.scrollToActiveTab();
      }
    });

    this.notifier.notifications$.subscribe((result) => {
      if (result === 'POOL.CLICK.CREATE_FARM') {
        this.notifier.clearNotifications();

        this.createFarm();
      }
    });
  }

  openConnect() {
    this.centerItem();

    if (this.tabDisabled) {
      return;
    }

    this.accountService.connect().subscribe();
  }

  centerItem() {
    this.isMobile$.subscribe((isMobile) => {
      if (!isMobile) {
        return;
      }

      this.scrollToActiveTab();
    });
  }

  onChangeFarm(id: number) {
    this.accountsService.switchAccount(id).subscribe(() => {
      this.scrollToActiveTab();
    });
  }

  scrollToActiveTab() {
    setTimeout(() => {
      const activeElement = document.querySelector('.active');
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }, 200);
  }

  createFarm() {
    this.farmSelect.close();

    const config = {
      image: 'assets/images/dashboard/dialogs/farm.svg',
      imageAlt: 'farm',
      title: 'WM_TECH_POOL.ACCOUNT.DIALOG.TITLE',
      app: 'pool',
      fields: [{ name: 'label', label: 'WM_TECH_MMS.FARM.LABEL', type: 'input', validators: [Validators.required] }],
    };

    const dialogRef = this.dialogService.openPopup(config);

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.label) {
        this.accountsService.createAccount(result?.label).subscribe(() => {
          this.alertService.showAlert(this.translateService.instant('FARMS.CREATE.SUCCESS'), 'success');
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.accountsSubscription) this.accountsSubscription.unsubscribe();
    if (this.userSubscription) this.userSubscription.unsubscribe();
  }
}
